import { Alert, Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag } from "@arco-design/web-react"
import { useNavigate, useParams } from "react-router-dom";
import { SOPOAPI, SOAPI, CustomerAPI } from "@/apis/sales";
import { Number } from "@/components/Numeric";
import { SOPOStatusDef, SOPOStatus, SOStatus } from "@/enums/sales";
import { EnumLabel } from "@/components/Enum";
import Reference from "@/components/Reference";
import SaleOrderLink from "@/components/SaleOrderLink";
import { useCallback, useEffect, useState } from "react";
import { FileUtil } from "@/utils";
import _ from "lodash";
import SendEmailButton from "@/components/SendEmailButton";

export const SOPickOrderInfo = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [info, setInfo] = useState({});
    const [so, setSO] = useState();
    const [printLoading, setPrintLoading] = useState(false);

    const back = () => {
        navigate("/workspace/sales/so-pick-order");
    }

    const getInfo = useCallback(async () => {
        const sopoInfo = await SOPOAPI.get(params.poId);
        const soInfo = await SOAPI.get(sopoInfo.soId);
        setSO(soInfo);
        _.forEach(sopoInfo.items, (item) => {
            item.soItemQty = _.find(soInfo.items, (soItem) => soItem.id === item.soItemId).quantity;
        })
        setInfo(sopoInfo);
    }, [params])

    const voidPO = async () => {
        try {
            await SOPOAPI.voidPO(params.poId);
            Message.success("Pick order voided successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    }

    useEffect(() => {
        getInfo();
    }, [getInfo])

    return (
        <>
            <Card title={<Space>
                <span>{info.pickOrderNo}</span>
                <Tag><EnumLabel enumObj={SOPOStatusDef} value={info.status} /></Tag>
            </Space>} extra={
                <Space>
                    {[SOStatus.Created, SOStatus.Issued, SOStatus.Picked, SOStatus.Partial, SOStatus.PartialFulfilledInvoiced].includes(so?.status) && info.status === SOPOStatus.Open &&
                        <Button type="primary" onClick={() => navigate(`/workspace/sales/so-fulfillment/so/${so?.id}?sopoId=${params.poId}`)}>Fulfill</Button>
                    }
                    {info.status === SOPOStatus.Open &&
                        <Button type="primary" onClick={() => navigate(`/workspace/sales/so-pick-order/${params.poId}/edit`)}>Edit</Button>
                    }
                    {info.status === SOPOStatus.Open &&
                        <Popconfirm title="Are you sure you want to void pick order?"
                            onOk={() => voidPO()}>
                            <Button type="primary" status="danger">Void</Button>
                        </Popconfirm>
                    }
                    {info.status === SOPOStatus.Open &&
                        <Button type="primary" onClick={async () => {
                            setPrintLoading(true);
                            try {
                                const res = await SOPOAPI.print(info.id);
                                FileUtil.download(res.data, `${info.pickOrderNo}.pdf`);
                            } catch (error) {
                                Message.error(error.message);
                            } finally {
                                setPrintLoading(false);
                            }
                        }} loading={printLoading}>Print</Button>
                    }
                    {info.status === SOPOStatus.Open &&
                        <SendEmailButton getEmailContent={async () => {
                            return await SOPOAPI.getEmail(info.id);
                        }} getToList={async () => {
                            const customer = await CustomerAPI.get(so.customerId);
                            return customer.contacts;
                        }} sendEmail={async (val) => {
                            await SOPOAPI.sendEmail(info.id, val);
                        }} />
                    }
                    <Button onClick={() => back()}>Back</Button>
                </Space>
            }>
                <Descriptions colon=":"
                    data={[{
                        label: "SO#",
                        value: <SaleOrderLink soId={info.soId}>{info.soNo}</SaleOrderLink>
                    }, {
                        label: "Customer Name",
                        value: info.customerName
                    }, {
                        label: "Address",
                        value: info.address
                    }, {
                        label: "SO Due Date",
                        value: info.soDueDate
                    }, {
                        label: "SO Creation Date",
                        value: info.soCreateDate
                    }, {
                        label: "SO Ready Date",
                        value: info.readyDate
                    }, {
                        label: "Pick Date",
                        value: info.createTime
                    }, {
                        label: "Fulfillment#",
                        value: info.fulfillment?.soFulfillmentNo
                    }, {
                        label: "Warehouse",
                        value: info.warehouseName
                    }, {
                        label: "Reference",
                        value: <Reference value={info.references} />,
                        span: 5
                    }]} />

                <Table rowKey="id" columns={[
                    {
                        title: "Line#", render: (col, record, index) => {
                            return <span>{index + 1}</span>
                        }
                    },
                    { title: "Item Number", dataIndex: "itemNo" },
                    { title: "Item Name", dataIndex: "itemName" },
                    { title: "Description", dataIndex: "description" },
                    { title: "SO Qty", render: (col, record) => <Number value={record.soItemQty} /> },
                    { title: "Pick Qty", render: (col, record) => <Number value={record.pickQty} /> },
                    { title: "UOM", dataIndex: "uom" },
                    {
                        title: "Alt Qty", render: (col, record) =>
                            <span><Number value={record.pickAltQty} /> {record.additionalUom}</span>
                    },
                    { title: "Warehouse Location", dataIndex: "locationName" },
                    { title: "Lot#", dataIndex: "lot" },
                    { title: "Expiration", dataIndex: "expiredDate" },
                ]}
                    data={info.items}
                />
            </Card>
            <Alert className="alert-bottom" content="Psst: Pick orders that are not fulfilled within 15 days will be voided by the system automatically to release the reserved inventory. You can manually void pick orders to release the inventory from reservation right away." />
        </>
    )
}
